/* eslint-disable comma-dangle */
import { watch } from "vue";
import { useGrowthBook } from "@/composables/useGrowthBook";
import { initObservability, isObservabilityInitialized } from "./init";
import { buildFeegowObservabilityConfig } from "@/utils/feegowObservability";

// Boolean kill switch. False default → RUM does NOT initialize at all, so no
// SDK init, no intake POSTs to datadoghq.eu, no billing. Flip to true in the
// GrowthBook UI to turn ingestion on for the next page load.
const FLAG_RUM_ENABLED = "NOA_browser_rum_enabled";

// Number (0..100) read as `getFeatureValue` fallback 0. Drives RUM
// `sessionSampleRate`. 0 = capture no sessions even when enabled (useful as a
// "armed but silent" state). Once the SDK initializes, this value is frozen
// for the page lifetime — changing it in GB only affects the next page load.
const FLAG_RUM_SAMPLE_RATE = "NOA_browser_rum_sample_rate";

/**
 * Reactively initialize Datadog browser observability when the GrowthBook
 * kill-switch flag flips on. Must be called once, after `initGrowthBook(...)`
 * has been issued so the watcher can pick up `isReady` and the flag values.
 *
 * Behaviour:
 *   - GB unreachable / flag missing → flag defaults to false → SDK never inits.
 *   - Flag on, sample rate 0 → SDK inits but captures 0 sessions (armed,
 *     silent). Lets you flip the flag on safely before raising the rate.
 *   - Flag on, sample rate > 0 → SDK inits with that rate, sessions flow.
 *   - Once initialized, further flag changes require a page reload (Datadog
 *     RUM SDK has no in-place re-init for `sessionSampleRate`).
 */
export function watchAndInitObservabilityWhenFlagOn(): void {
  const { isReady, isOn, getFeatureValue } = useGrowthBook();
  const rumEnabled = isOn(FLAG_RUM_ENABLED);
  const rumSampleRate = getFeatureValue<number>(FLAG_RUM_SAMPLE_RATE, 0);

  watch(
    [isReady, rumEnabled, rumSampleRate],
    ([ready, enabled, sampleRate]) => {
      if (!ready || !enabled) return;
      if (isObservabilityInitialized()) return;

      const config = buildFeegowObservabilityConfig();
      initObservability({
        ...config,
        sessionSampleRate: clampSampleRate(sampleRate),
      });
    },
    { immediate: true }
  );
}

function clampSampleRate(value: unknown): number {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  if (num < 0) return 0;
  if (num > 100) return 100;
  return num;
}
